'use client'

import { memo, useMemo } from 'react'
import {
  DELIVERY_STOPS,
  ROUTE,
  ROUTE_LEGS,
  stopStatusAt,
  type RouteLegKey,
  type TrackerCopy,
  type TrackerLocale,
  type TrackerState,
} from '@/lib/santa-tracker'
import { formatLocalTime } from './format'

/**
 * El recorrido en forma de lista.
 *
 * Cumple dos funciones a la vez y por eso no es un extra escondido:
 *
 * 1. **Alternativa textual al mapa.** Es el camino accesible por teclado y
 *    lector de pantalla (los marcadores del SVG no son focalizables a
 *    propósito, ver `tracker-map.tsx`), y también el plan B cuando el mapa se
 *    ve pequeño.
 * 2. **Contenido indexable.** Sesenta topónimos y trece regiones en HTML plano,
 *    que ningún buscador podría leer de un `<path>`.
 */

const STOPS_BY_LEG = ROUTE_LEGS.reduce<Record<string, typeof DELIVERY_STOPS>>((acc, leg) => {
  acc[leg] = DELIVERY_STOPS.filter((stop) => stop.leg === leg)
  return acc
}, {})

const STATUS_DOT: Record<'visited' | 'current' | 'upcoming', string> = {
  visited: 'bg-amber-300',
  current: 'bg-white',
  upcoming: 'bg-sky-300/50',
}

const CHIP: Record<'visited' | 'current' | 'upcoming', string> = {
  visited: 'border-amber-300/40 bg-amber-400/10 text-amber-100',
  current: 'border-white/70 bg-white/20 text-white',
  upcoming: 'border-white/10 bg-white/5 text-white/70',
}

function LegBlock({
  leg,
  copy,
  locale,
  state,
  onSelectStop,
}: {
  leg: RouteLegKey
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  onSelectStop: (stopId: string) => void
}) {
  const stops = STOPS_BY_LEG[leg] ?? []
  if (stops.length === 0) return null

  return (
    <li className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <h3 className="font-christmas text-lg font-semibold capitalize text-white">
        {copy.status.legLabel[leg]}
      </h3>
      <ul className="mt-3 flex flex-wrap gap-2">
        {stops.map((stop) => {
          const status = stopStatusAt(stop, state)
          return (
            <li key={stop.id}>
              <button
                type="button"
                onClick={() => onSelectStop(stop.id)}
                className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition hover:bg-white/15 ${CHIP[status]}`}
              >
                <span aria-hidden className={`h-2 w-2 rounded-full ${STATUS_DOT[status]}`} />
                <span>{copy.stops[stop.id]}</span>
                <span className="tabular-nums text-xs opacity-70">
                  {formatLocalTime(state.nowMs, stop.utcOffset, locale)}
                </span>
                <span className="sr-only">{copy.map.stopStatus[status]}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </li>
  )
}

export interface TrackerRouteListProps {
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  onSelectStop: (stopId: string) => void
}

export const TrackerRouteList = memo(function TrackerRouteList({
  copy,
  locale,
  state,
  onSelectStop,
}: TrackerRouteListProps) {
  // Los dos extremos son el Polo Norte, que no es una parada de entrega.
  const legs = useMemo(
    () => ROUTE_LEGS.filter((leg) => leg !== 'north-pole' && leg !== 'north-pole-return'),
    []
  )

  return (
    <section aria-labelledby="tracker-route-heading" className="space-y-4">
      <h2 id="tracker-route-heading" className="font-christmas text-2xl font-semibold md:text-3xl">
        {copy.map.listHeading}
      </h2>
      <p className="text-base text-white/75">{copy.map.listIntro}</p>
      <ol className="grid gap-3 md:grid-cols-2">
        {legs.map((leg) => (
          <LegBlock
            key={leg}
            leg={leg}
            copy={copy}
            locale={locale}
            state={state}
            onSelectStop={onSelectStop}
          />
        ))}
      </ol>
    </section>
  )
})
